import {
  Flex,
  type LedgerAccountTypeEnum,
  MixpanelEvent,
  MixpanelEventProperty,
  type MixpanelEventSource,
  Modal,
  PopperHeader,
  useDisclosure,
  useDynamicCallback,
  useMixpanel,
} from '@talos/kyoko';
import { createContext, useContext, useMemo, useState } from 'react';
import { AccountLedgerEvents } from '../containers/Trading/Markets/AccountLedgerEvents/AccountLedgerEvents';

type PositionDetailsModalParams = {
  asset?: string;
  account: string;
  accountType: LedgerAccountTypeEnum;
  source?: MixpanelEventSource;
};

interface PositionDetailsModalContextProps {
  /** Open the modal to show details for the provided params */
  open(params: PositionDetailsModalParams): void;
}

const PositionDetailsModalContext = createContext<PositionDetailsModalContextProps | null>(null);

export const usePositionDetailsModal = () => {
  const context = useContext(PositionDetailsModalContext);
  if (context == null) {
    throw new Error('Missing PositionDetailsModalContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
};

export const PositionDetailsModalProvider = function PositionDetailsModalProvider(
  props: React.PropsWithChildren<unknown>
) {
  const mixpanel = useMixpanel();
  const [openedPositionParams, setOpenedPositionParams] = useState<PositionDetailsModalParams>();
  const disclosure = useDisclosure({ onClose: () => setOpenedPositionParams(undefined) });

  const open = useDynamicCallback((params: PositionDetailsModalParams) => {
    mixpanel.track(MixpanelEvent.OpenLedgerEventsModal, {
      [MixpanelEventProperty.Source]: params.source,
    });
    setOpenedPositionParams(params);

    disclosure.open();
  });

  const value = useMemo(() => ({ open }), [open]);

  return (
    <PositionDetailsModalContext.Provider value={value}>
      {props.children}
      {/* For simplicity, just conditionally render this modal component. Animations are a bit worse but thats acceptable */}
      {disclosure.isOpen && openedPositionParams != null && (
        <Modal
          {...disclosure}
          minHeight="90vh"
          maxHeight="90vh"
          minWidth="90vw"
          maxWidth="90vw"
          position="relative"
          data-testid="position-details-modal"
        >
          <Flex flexDirection="column" position="absolute" top="0" bottom="0" right="0" left="0">
            <PopperHeader title="Position History" onCloseClicked={() => disclosure.close()} />
            <AccountLedgerEvents {...openedPositionParams} />
          </Flex>
        </Modal>
      )}
    </PositionDetailsModalContext.Provider>
  );
};
